(() => {
  if (window.DUKAlgoliaShared) return;

  const AUSTRIA_HOST = /(^|\.)duftundkultur\.at$/i;

  function getLocaleKey() {
    const pathname = window.location.pathname.toLowerCase();
    const pathLocale = pathname.match(/^\/([a-z]{2})-([a-z]{2,3})(\/|$)/);
    if (pathLocale) return `${pathLocale[1]}-${pathLocale[2]}`;

    const htmlLang = document.documentElement.lang?.toLowerCase() || '';
    const isAustriaDomain = AUSTRIA_HOST.test(window.location.hostname);
    const language = pathname.match(/^\/(de|en)(\/|$)/)?.[1]
      || String(window.Shopify?.locale || htmlLang).toLowerCase().split('-')[0];

    if (isAustriaDomain && (language === 'de' || language === 'en')) return `${language}-at`;
    if (htmlLang.includes('-')) return htmlLang;
    return language === 'de' ? 'de-at' : 'en-int';
  }

  function getLanguage() {
    return getLocaleKey().startsWith('de') ? 'de' : 'en';
  }

  function translate(value) {
    if (typeof value === 'string') return value;
    return value?.[getLanguage()] || value?.en || value?.de || '';
  }

  function getMarketContext() {
    const pathname = window.location.pathname.toLowerCase();
    const pathMatch = pathname.match(/^\/([a-z]{2})-([a-z]{2,3})(?:\/|$)/);
    const language = String(
      pathMatch?.[1]
      || window.Shopify?.locale
      || document.documentElement.lang
      || 'en'
    ).toLowerCase().split('-')[0] === 'de' ? 'de' : 'en';

    let country = String(pathMatch?.[2] || '').toUpperCase();
    if (country === 'INT') country = '';
    if (!country && AUSTRIA_HOST.test(window.location.hostname)) country = 'AT';
    if (!country) country = String(window.Shopify?.country || '').toUpperCase();

    return { language, country };
  }

  function getActiveCurrency() {
    return String(window.Shopify?.currency?.active || '').toUpperCase();
  }

  function getConfiguredMarketSuffix(country, language) {
    const mapping = Array.isArray(window.algoliaShopify?.config?.markets_mapping)
      ? window.algoliaShopify.config.markets_mapping
      : [];

    const getRegions = (entry) => {
      const value = entry?.regions || entry?.countries || entry?.country_codes || entry?.countryCodes || [];
      return (Array.isArray(value) ? value : [value]).map((region) => String(region || '').toUpperCase());
    };

    const getEntryLanguage = (entry) => String(
      entry?.locale || entry?.language || entry?.language_code || entry?.languageCode || ''
    ).toLowerCase().split('-')[0];

    const exact = mapping.find((entry) =>
      getRegions(entry).includes(country) && getEntryLanguage(entry) === language
    );

    return exact?.suffix || '';
  }

  function getMarketSuffix() {
    const { country, language } = getMarketContext();
    if (country !== 'CH') return '';
    return getConfiguredMarketSuffix('CH', language) || `switzerland_${language}`;
  }

  function buildIndexCandidates(kind) {
    const prefix = window.algoliaShopify?.config?.index_prefix || 'shopify_';
    if (kind === 'stores') return [`${prefix}stores`];
    if (!['products', 'collections'].includes(kind)) return [];

    const baseIndex = `${prefix}${kind}`;
    const { country, language } = getMarketContext();

    // Four-index model:
    // 1. unsuffixed German/EUR index
    // 2. Austria English/EUR index
    // 3. Switzerland German/CHF index
    // 4. Switzerland English/CHF index
    if (country === 'CH') {
      return [
        `${baseIndex}_${getMarketSuffix()}`,
        language === 'en' ? `${baseIndex}_austria_en` : baseIndex
      ];
    }
    if (language === 'en') return [`${baseIndex}_austria_en`];
    return [baseIndex];
  }

  function shouldShowPrices() {
    const { country } = getMarketContext();
    const currency = getActiveCurrency();
    if (country === 'CH') return currency === 'CHF';
    return currency === 'EUR';
  }

  function localizePath(path) {
    const cleanPath = `/${String(path || '').replace(/^\/+/, '')}`;
    const shopifyRoot = window.Shopify?.routes?.root;

    if (typeof shopifyRoot === 'string' && shopifyRoot) {
      return `${shopifyRoot.replace(/\/$/, '')}${cleanPath}`.replace(/\/+/g, '/');
    }

    const currentPrefix = window.location.pathname.toLowerCase().match(/^\/([a-z]{2}(?:-[a-z]{2,3})?)(\/|$)/)?.[1];
    return currentPrefix ? `/${currentPrefix}${cleanPath}` : cleanPath;
  }

  function localizeHitUrl(directUrl, fallbackPath) {
    if (!directUrl) return localizePath(fallbackPath);

    try {
      const url = new URL(directUrl, window.location.origin);
      const resourcePath = url.pathname.replace(/^\/[a-z]{2}(?:-[a-z]{2,3})?(?=\/)/i, '');
      return `${localizePath(resourcePath)}${url.search}${url.hash}`;
    } catch (_error) {
      return localizePath(fallbackPath);
    }
  }

  function getValue(object, paths, fallback = '') {
    for (const path of paths) {
      if (object && Object.prototype.hasOwnProperty.call(object, path)
        && object[path] !== undefined && object[path] !== null && object[path] !== '') return object[path];
      const nested = String(path).split('.').reduce(
        (value, key) => value && value[key] !== undefined ? value[key] : undefined,
        object
      );
      if (nested !== undefined && nested !== null && nested !== '') return nested;
    }
    return fallback;
  }

  function getRawPriceBounds(hit) {
    const currency = getActiveCurrency();
    const marketPricingField = currency ? `market_pricing_${currency.toLowerCase()}` : '';
    const marketMin = marketPricingField ? [`${marketPricingField}.variants_min_price`, `${marketPricingField}.price`] : [];
    const marketMax = marketPricingField ? [`${marketPricingField}.variants_max_price`, `${marketPricingField}.price`] : [];

    const min = Number(getValue(hit, [
      ...marketMin,
      'price_min',
      'min_price',
      'variants_min_price',
      'price',
      'default_variant_price'
    ], NaN));

    const max = Number(getValue(hit, [
      ...marketMax,
      'price_max',
      'max_price',
      'variants_max_price',
      'price',
      'default_variant_price'
    ], min));

    return { min, max };
  }

  function getRawPrice(hit) {
    return getRawPriceBounds(hit).min;
  }

  function productPriceVaries(hit) {
    const { min, max } = getRawPriceBounds(hit);
    return Number.isFinite(min) && Number.isFinite(max) && max > min;
  }

  function formatPrice(hit) {
    if (!shouldShowPrices()) return '';

    const currency = getActiveCurrency();
    const rawPrice = getRawPrice(hit);
    if (!currency || !Number.isFinite(rawPrice)) return '';

    const amount = new Intl.NumberFormat('de-DE', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2
    }).format(rawPrice);

    const currencyParts = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 2
    }).formatToParts(rawPrice);

    const numericPartTypes = new Set(['integer', 'group', 'decimal', 'fraction']);
    const firstNumericPart = currencyParts.findIndex((part) => numericPartTypes.has(part.type));
    let lastNumericPart = -1;

    for (let index = currencyParts.length - 1; index >= 0; index -= 1) {
      if (numericPartTypes.has(currencyParts[index].type)) {
        lastNumericPart = index;
        break;
      }
    }

    if (firstNumericPart === -1 || lastNumericPart === -1) return amount;

    const prefix = currencyParts.slice(0, firstNumericPart).map((part) => part.value).join('');
    const suffix = currencyParts.slice(lastNumericPart + 1).map((part) => part.value).join('');
    return `${prefix}${amount}${suffix}`;
  }

  function formatProductPrice(hit) {
    const price = formatPrice(hit);
    if (!price) return '';
    if (!productPriceVaries(hit)) return price;
    return `${translate({ de: 'ab', en: 'From' })} ${price}`;
  }

  window.DUKAlgoliaShared = Object.freeze({
    getLocaleKey,
    getLanguage,
    translate,
    getMarketContext,
    getActiveCurrency,
    getMarketSuffix,
    buildIndexCandidates,
    shouldShowPrices,
    localizePath,
    localizeHitUrl,
    getRawPriceBounds,
    getRawPrice,
    productPriceVaries,
    formatPrice,
    formatProductPrice
  });
})();
